"use client";

import { ReactNode } from "react";
import {
  Plane,
  Car,
  Hotel,
  Ship,
  MapPin,
  Coffee,
  Utensils,
  Camera,
  Palmtree,
  Bus,
  Train,
} from "lucide-react";
import { usePrimaryColorStyles } from "../contexts/use-primary-color-styles";
import { useQuoteThemeStyles } from "../quote-theme/use-quote-theme-styles";
import { quoteThemeColorStyles } from "../quote-theme/quote-theme-css-vars";

// Add this export for use in forms
export const ItineraryIcons = {
  plane: Plane,
  car: Car,
  hotel: Hotel,
  ship: Ship,
  location: MapPin,
  activity: Coffee,
  dining: Utensils,
  sightseeing: Camera,
  beach: Palmtree,
  bus: Bus,
  train: Train,
} as const;

export type ItineraryIconType = keyof typeof ItineraryIcons;

export interface TimelineContainerProps {
  children: ReactNode;
  title: string;
  icon: ItineraryIconType;
  primaryColor?: string;
  /** When false, hide the left timeline icon rail (Brochure Mode). */
  showChrome?: boolean;
}

export function TimelineContainer({
  children,
  title,
  icon,
  showChrome = true,
}: TimelineContainerProps) {
  const { containerStyle, textIconStyle } = usePrimaryColorStyles();
  const { classNames } = useQuoteThemeStyles();
  const IconComponent = ItineraryIcons[icon];

  if (!showChrome) {
    return <div className={`${classNames.card} relative`}>{children}</div>;
  }

  return (
    <div className="relative">
      {/* Desktop Timeline */}
      <div className="hidden sm:block absolute left-0 top-0 bottom-0 w-24">
        <div className="sticky top-24 flex flex-col items-center">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={containerStyle}
          >
            <IconComponent className="w-6 h-6" style={textIconStyle} />
          </div>
          <div
            className="mt-2 text-sm font-medium text-center"
            style={quoteThemeColorStyles.primary}
          >
            {title}
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="sm:hidden flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={containerStyle}
        >
          <IconComponent className="w-5 h-5" style={textIconStyle} />
        </div>
        <div className="text-sm font-medium">
          {title}
        </div>
      </div>

      {/* Content */}
      <div className={`sm:ml-24 ${classNames.card} relative`}>{children}</div>
    </div>
  );
}
